import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import PageMeta from "@/components/feature/PageMeta";

export default function ResetPassword() {
  const branding = useBranding();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!email || !token) {
      setError("Enlace inválido o incompleto.");
    }
  }, [email, token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !token) return;
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.resetPassword({ email, token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err: any) {
      setError(err.message || "Error al restablecer la contraseña. Es posible que el enlace haya expirado.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#080f1a] overflow-hidden">
      <PageMeta path="/reset-password" />
      
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-16 lg:px-24 z-10">
        <Link to="/" className="absolute top-8 left-8 text-white/50 hover:text-white flex items-center gap-2 text-sm transition-colors">
          <i className="ri-arrow-left-line"></i> Volver al inicio
        </Link>
        <div className="max-w-md w-full mx-auto">
          <div className="mb-10 text-center">
            <img src={branding.logo_full} alt={branding.site_name} className="h-14 mx-auto mb-8" />
            <h1 className="text-3xl font-bold text-white mb-3">Crear nueva contraseña</h1>
            <p className="text-[var(--color-text-secondary)]">Ingresa tu nueva contraseña para {email}</p>
          </div>

          {error && (
            <div className="mb-6 bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl text-sm flex items-center gap-3">
              <i className="ri-error-warning-line text-lg"></i>
              <p>{error}</p>
            </div>
          )}

          {success ? (
            <div className="text-center bg-green-500/10 border border-green-500/20 rounded-xl p-8 animate-[fadeIn_0.5s_ease-out]">
              <div className="w-16 h-16 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Contraseña actualizada</h3>
              <p className="text-[var(--color-text-secondary)] mb-6">Serás redirigido al inicio de sesión...</p>
              <Link to="/login" className="btn-secondary w-full flex items-center justify-center gap-2">
                Ir a Iniciar Sesión ahora
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Nueva Contraseña</label>
                <div className="relative">
                  <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"></i>
                  <input
                    type="password"
                    required
                    className="input-field pl-11 py-3"
                    placeholder="Mínimo 6 caracteres"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              <button type="submit" disabled={loading || !email || !token} className="btn-primary w-full flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 py-3">
                {loading ? <i className="ri-loader-4-line animate-spin"></i> : <><i className="ri-save-line"></i> Guardar contraseña</>}
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="hidden lg:block w-1/2 relative bg-[var(--color-bg-card)]">
        <div className="absolute inset-0 bg-gradient-to-r from-[#080f1a] via-transparent to-transparent z-10"></div>
        <img src={branding.hero_login || "https://images.unsplash.com/photo-1542393046-277e9a0cfaee?auto=format&fit=crop&q=80"} alt="Capacitación" className="absolute inset-0 w-full h-full object-cover opacity-60 mix-blend-overlay" />
      </div>
    </div>
  );
}
